/**
 * Hỏi thăm việc nền của một lượt bói chỉ tay (quét / luận).
 *
 * Từ v92 hai bước nặng chạy nền vì chúng mất 4–5 phút — dài hơn hẳn mọi mức timeout
 * nằm giữa trình duyệt và BE (Cloudflare cắt ~100s, nginx mặc định 60s). Giữ chúng
 * trong một request đồng bộ nghĩa là tiền đã trừ, LLM vẫn chạy tới cùng, và người xem
 * mất kết nối trước khi thấy kết quả.
 */

import { useQuery } from "@tanstack/react-query";

import { getPalmJob, type PalmJobStage } from "@/lib/boide-api";

/** Nhịp hỏi thăm: đủ mượt để thanh tiến độ nhúc nhích, đủ thưa để không phí request. */
export const PALM_JOB_POLL_MS = 3000;

/**
 * @param stage Giai đoạn bên gọi ĐANG CHỜ — nằm trong `queryKey`, xem giải thích dưới.
 */
export function usePalmJob(draftId: string | null, enabled: boolean, stage: PalmJobStage) {
  return useQuery({
    /**
     * 🔴 `stage` PHẢI nằm trong khoá, không chỉ trong dữ liệu trả về.
     *
     * Một bản nháp đi qua HAI giai đoạn (quét rồi luận) và trước đây cả hai dùng chung
     * một khoá. Hậu quả đo được: quét xong, cache giữ `{stage:"scan", state:"done"}`;
     * người xem bấm luận giải thì react-query trả NGAY bản cache ấy trước khi nhịp
     * fetch đầu về, nên màn chờ hiện "Đã xong rồi!" và FE đi mở một bài luận chưa tồn
     * tại — đúng triệu chứng "ấn luận giải xong nó báo đã xong mà không có bài".
     *
     * Tách khoá theo giai đoạn thì kết quả của giai đoạn TRƯỚC không có đường nào chảy
     * sang giai đoạn SAU. Cái giá: lúc vừa chuyển giai đoạn, khoá mới chưa có cache nên
     * `data` là `undefined` một nhịp — bên gọi phải coi đó là "đang chạy", không phải
     * "chưa có việc".
     */
    queryKey: ["palm-job", draftId, stage],
    queryFn: () => getPalmJob(draftId as string),
    enabled: Boolean(draftId) && enabled,
    /**
     * 🔴 SỐ CỐ ĐỊNH, không phải hàm đọc `query.state.data`.
     *
     * Bản đầu dùng dạng hàm để "tự dừng khi xong". Nghe gọn, nhưng nó buộc nhịp poll
     * phụ thuộc vào ĐÚNG một lần đọc state — trả `false` một lần là vòng poll tắt
     * hẳn, và nếu lần đọc ấy không phải `running` (chưa có data, vừa lỗi mạng, hoặc
     * observer thứ hai vừa mount) thì người xem ngồi trước một màn chờ không bao giờ
     * cập nhật nữa. Đo được thật: bài luận xong mà FE không tự mở, người xem phải vào
     * lịch sử tìm.
     *
     * Việc DỪNG giao cho `enabled` — nơi bên gọi biết chắc mình còn cần câu trả lời
     * hay không (`!scan || waitingReading`, hoặc sổ theo dõi của watcher). Sai số tệ
     * nhất là một lượt gọi thừa sau khi xong; đổi lại vòng poll không bao giờ tự tắt
     * oan.
     */
    refetchInterval: PALM_JOB_POLL_MS,
    // Đây đúng là ca người xem chuyển tab đi làm việc khác; quay lại thì thấy kết quả
    // sẵn, không phải chờ thêm một nhịp poll.
    refetchIntervalInBackground: true,
    // Việc nền sống lâu hơn một lần mất mạng thoáng qua — đừng bỏ cuộc sớm.
    retry: 3,
    // Trạng thái việc đổi từng giây: bản cache cũ ở đây luôn sai. `gcTime` thì để mặc
    // định — `gcTime: 0` xoá query ngay khi hụt observer một nhịp, tức chính lúc màn
    // chờ chuyển giữa hai bước.
    staleTime: 0,
  });
}
